import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { LoaderCircle, Volume2, X } from "lucide-react";
import { requestJapaneseSpeech } from "./api";
import { toHiragana } from "./japanese";
import {
  chooseJapaneseVoice,
  japaneseSpeechLanguage,
  nativeSpeechWaitMs,
  ownsActiveSpeech,
  startJapaneseSpeechFlow,
  type JapaneseSpeechStatus,
  type ManagedSpeechPlayback
} from "./speech";
import type { KanjiDetail } from "./types";

interface ActiveSpeech {
  stop: () => void;
  reset: () => void;
}

let activeSpeech: ActiveSpeech | null = null;
let speechAudioContext: AudioContext | null = null;

function stopActiveSpeech() {
  if (!activeSpeech) return;
  const current = activeSpeech;
  activeSpeech = null;
  current.stop();
  current.reset();
}

function unlockSpeechAudio(): Promise<void> | null {
  const AudioContextClass = window.AudioContext
    ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return null;
  speechAudioContext ??= new AudioContextClass();
  return speechAudioContext.state === "running" ? Promise.resolve() : speechAudioContext.resume();
}

function webAudioPlayback(audioBlob: Blob, audioReady: Promise<void> | null): Promise<ManagedSpeechPlayback> {
  return (async () => {
    if (!speechAudioContext || !audioReady) throw new Error("Web Audio is unavailable");
    await audioReady;
    if (speechAudioContext.state !== "running") await speechAudioContext.resume();
    const audioBuffer = await speechAudioContext.decodeAudioData(await audioBlob.arrayBuffer());
    const source = speechAudioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(speechAudioContext.destination);
    let settled = false;
    let resolveFinished!: () => void;
    let rejectFinished!: (error: unknown) => void;
    const finished = new Promise<void>((resolve, reject) => {
      resolveFinished = resolve;
      rejectFinished = reject;
    });
    const settle = (error?: unknown) => {
      if (settled) return;
      settled = true;
      source.disconnect();
      if (error) rejectFinished(error);
      else resolveFinished();
    };
    source.onended = () => settle();
    try {
      source.start();
    } catch (error) {
      settle(error);
      throw error;
    }
    return {
      stop: () => {
        try { source.stop(); } catch { /* Playback has already ended. */ }
        settle();
      },
      finished
    };
  })();
}

export function JapaneseSpeechButton({
  text,
  reading,
  mode,
  label,
  compact = false,
  className = ""
}: {
  text: string;
  reading?: string;
  mode: "sentence" | "word";
  label: string;
  compact?: boolean;
  className?: string;
}) {
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [status, setStatus] = useState<JapaneseSpeechStatus>("idle");
  const [playbackFailed, setPlaybackFailed] = useState(false);
  const ownedSpeech = useRef<ActiveSpeech | null>(null);
  const failureTimer = useRef<number | null>(null);
  const mounted = useRef(true);
  const supported = "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;

  useEffect(() => {
    if (!("speechSynthesis" in window) || !("SpeechSynthesisUtterance" in window)) return;
    const refresh = () => setVoice(chooseJapaneseVoice(window.speechSynthesis.getVoices()));
    refresh();
    window.speechSynthesis.addEventListener("voiceschanged", refresh);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", refresh);
  }, []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (failureTimer.current !== null) window.clearTimeout(failureTimer.current);
      if (activeSpeech === ownedSpeech.current) stopActiveSpeech();
      else ownedSpeech.current?.stop();
    };
  }, []);

  if (!text.trim()) return null;
  const updateStatus = (next: JapaneseSpeechStatus) => {
    if (mounted.current) setStatus(next);
  };
  const devicePlayback = (): ManagedSpeechPlayback => {
    if (!supported) throw new Error("Device speech is unavailable");
    window.speechSynthesis.cancel();
    const spokenText = mode === "word" && reading?.trim() ? reading : text;
    const utterance = new SpeechSynthesisUtterance(spokenText);
    const currentVoice = voice ?? chooseJapaneseVoice(window.speechSynthesis.getVoices());
    if (currentVoice) utterance.voice = currentVoice;
    utterance.lang = japaneseSpeechLanguage(currentVoice);
    utterance.rate = 0.88;
    let resolveFinished!: () => void;
    let rejectFinished!: (error: unknown) => void;
    let settled = false;
    const finished = new Promise<void>((resolve, reject) => {
      resolveFinished = resolve;
      rejectFinished = reject;
    });
    const settle = (error?: unknown) => {
      if (settled) return;
      settled = true;
      if (error) rejectFinished(error);
      else resolveFinished();
    };
    utterance.onend = () => settle();
    utterance.onerror = (event) => settle(new Error(`Device speech failed (${event.error})`));
    window.speechSynthesis.speak(utterance);
    // WebKit occasionally leaves its queue paused after cancel().
    if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    return {
      stop: () => {
        window.speechSynthesis.cancel();
        settle();
      },
      finished,
      // iOS WebKit may otherwise collect the utterance before its callbacks fire.
      retained: utterance
    };
  };
  const speak = () => {
    if (failureTimer.current !== null) window.clearTimeout(failureTimer.current);
    failureTimer.current = null;
    setPlaybackFailed(false);
    if (ownsActiveSpeech(activeSpeech, ownedSpeech.current)) {
      stopActiveSpeech();
      return;
    }
    stopActiveSpeech();
    if (supported) window.speechSynthesis.cancel();
    const audioReady = unlockSpeechAudio();
    // iOS WebKit grants media playback from this tap, but a rejected resume
    // promise may settle before the late Qwen response is decoded. Consume it
    // here and let webAudioPlayback turn the same failure into device speech.
    void audioReady?.catch(() => undefined);
    const ownership: ActiveSpeech = {
      stop: () => undefined,
      reset: () => updateStatus("idle")
    };
    ownedSpeech.current = ownership;
    activeSpeech = ownership;
    const flow = startJapaneseSpeechFlow({
      fallbackAfterMs: nativeSpeechWaitMs(mode),
      requestNative: (signal) => requestJapaneseSpeech({ text, reading, mode }, signal),
      playNative: (audioBlob) => webAudioPlayback(audioBlob, audioReady),
      playDeviceFallback: devicePlayback,
      onStatus: updateStatus
    });
    ownership.stop = flow.stop;
    void flow.finished
      .then((outcome) => {
        if (outcome !== "failed" || ownedSpeech.current !== ownership || !mounted.current) return;
        setPlaybackFailed(true);
        failureTimer.current = window.setTimeout(() => {
          failureTimer.current = null;
          if (mounted.current) setPlaybackFailed(false);
        }, 4_000);
      })
      .finally(() => {
        if (activeSpeech === ownership) activeSpeech = null;
        if (ownedSpeech.current === ownership) ownedSpeech.current = null;
      });
  };
  const failureLabel = "Couldn’t play — tap to retry";
  return (
    <button
      className={`japanese-speech-button ${compact ? "is-compact" : ""} ${status === "playing" ? "is-speaking" : ""} ${status === "generating" ? "is-loading" : ""} ${status === "fallback" ? "is-fallback" : ""} ${playbackFailed ? "is-failed" : ""} ${className}`.trim()}
      type="button"
      onClick={speak}
      aria-label={playbackFailed ? failureLabel : label}
      aria-live="polite"
      aria-busy={status === "generating"}
      aria-pressed={status === "playing" || status === "fallback"}
      title={playbackFailed
        ? "Qwen and this device could not play the audio. Tap to try again."
        : status === "fallback" ? "Qwen voice unavailable — using this device" : label}
    >
      {playbackFailed
        ? compact ? <Volume2 size={18} /> : <span>{failureLabel}</span>
        : status === "generating" ? <LoaderCircle className="spin" size={18} /> : <Volume2 size={18} />}
    </button>
  );
}

export function InspectableJapaneseText({
  text,
  onInspect,
  children
}: {
  text: string;
  onInspect: (literal: string) => void;
  children?: ReactNode;
}) {
  const timer = useRef<number | null>(null);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    pointerStart.current = null;
  };
  useEffect(() => cancel, []);

  const begin = (event: ReactPointerEvent<HTMLSpanElement>, literal: string) => {
    cancel();
    pointerStart.current = { x: event.clientX, y: event.clientY };
    timer.current = window.setTimeout(() => {
      timer.current = null;
      pointerStart.current = null;
      onInspect(literal);
    }, 520);
  };
  const move = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (!pointerStart.current) return;
    if (Math.hypot(event.clientX - pointerStart.current.x, event.clientY - pointerStart.current.y) > 8) cancel();
  };

  return (
    <span className="inspectable-japanese-text" lang="ja">
      {[...text].map((literal, index) => /\p{Script=Han}/u.test(literal) ? (
        <span
          className="inspectable-kanji"
          data-kanji={literal}
          key={`${literal}-${index}`}
          onPointerDown={(event) => begin(event, literal)}
          onPointerMove={move}
          onPointerUp={cancel}
          onPointerCancel={cancel}
          onContextMenu={(event) => {
            event.preventDefault();
            onInspect(literal);
          }}
        >{literal}</span>
      ) : literal)}
      {children}
    </span>
  );
}

export function KanjiDetailPopover({
  literal,
  detail,
  loading,
  error,
  onClose
}: {
  literal: string;
  detail: KanjiDetail | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) {
  return (
    <section className="kanji-detail-popover" role="dialog" aria-label={`Details for ${literal}`}>
      <button type="button" onClick={onClose} aria-label="Close kanji details"><X size={17} /></button>
      <div className="kanji-detail-literal" lang="ja">{literal}</div>
      {loading ? (
        <p className="kanji-detail-status"><LoaderCircle className="spin" size={17} /> Looking up this kanji…</p>
      ) : detail ? (
        <div className="kanji-detail-copy">
          <strong>{detail.meanings.slice(0, 4).join(" · ") || "No English meaning listed"}</strong>
          {detail.onReadings.length > 0 && <p><span>On</span> <b lang="ja">{detail.onReadings.map(toHiragana).join("、")}</b></p>}
          {detail.kunReadings.length > 0 && <p><span>Kun</span> <b lang="ja">{detail.kunReadings.map(toHiragana).join("、")}</b></p>}
          <small>{[
            detail.strokeCount ? `${detail.strokeCount} strokes` : null,
            detail.grade ? `school grade ${detail.grade}` : null,
            detail.frequency ? `frequency #${detail.frequency}` : null
          ].filter(Boolean).join(" · ")}</small>
          <a href="https://www.edrdg.org/wiki/KANJIDIC_Project.html" target="_blank" rel="noreferrer">KANJIDIC2 · EDRDG</a>
        </div>
      ) : <p className="kanji-detail-status">{error ?? "No kanji details found."}</p>}
    </section>
  );
}
